import React, {useCallback, useEffect, useState} from 'react';
import UnitComponent from '../unit/UnitComponent';
import {FLOOR_TYPES} from '../../constants/floorTypes';
import axios from "axios";
import {API_ROOT as API} from "../../config";
import {mapResponseDtoToModel} from "./UnitDetailsMapper";
import {LoadingIndicator} from "./component/LoadingIndicator";
import {UnitsContainerHeader} from "./component/UnitsContainerHeader";
import {compareUnits} from "../unit/support/UnitComparator";

const UnitManagement = ({ building }) => {
  const [selectedFloorTab, setSelectedFloorTab] = useState(0);
  const [unitTechnicalData, setUnitTechnicalData] = useState([]);
  const [units, setUnits] = useState([]);
  const [isLoadingFromBackend, setIsLoadingFromBackend] = useState(true);

  useEffect(() => {
    const loadTechnicalComponentsFromBackend = async () => {
      if (!building?.demand_point_id) {
        setIsLoadingFromBackend(false);
        return;
      }
      let cancelled = false;
      axios.get(`${API}/demand-points/${building?.demand_point_id}/units`)
          .then(res => {
            if (!cancelled) {
              setUnitTechnicalData(res.data);
            }
          })
          .catch(err => console.error('[detail] fetch failed:', err))
          .finally(() => {
            if (!cancelled) {
              setIsLoadingFromBackend(false);
            }
          });
      return () => { cancelled = true; };
    };

    loadTechnicalComponentsFromBackend();
  }, [building]);

  useEffect(() => {
    setUnits(unitTechnicalData?.map(unit => mapResponseDtoToModel(unit)));
  }, [unitTechnicalData]);

  useEffect(() => {
    const availableFloors = building?.floors?.filter(floor => floor.floorType !== 'unbekannt') || [];
    if (availableFloors.length > 0 && selectedFloorTab >= availableFloors.length) {
      setSelectedFloorTab(0);
    }
  }, [building?.floors]);

  const handleUnitDelete = useCallback((deletedUnitId) => {
    setUnits(prev => prev.filter(u => u.id !== deletedUnitId));
  }, []);

  const saveUnitDocumentsToBackend = async (documentsToSave = null) => {
    // PATCH /api/buildings/{id}/technical-components does not exist on the backend.
    // Unit document data is persisted to localStorage only.
    const docsToSave = documentsToSave || unitDocuments;
    const storageKey = `building-${building?.demand_point_id || building?.id}-unit-technical-data`;
    try {
      const merged = { ...unitTechnicalData, unit_documents: docsToSave };
      localStorage.setItem(storageKey, JSON.stringify(merged));
    } catch (err) {
      console.error('UnitManagement: failed to persist unit documents to localStorage', err);
    }
  };

  return (
    <div className="space-y-6">
      {isLoadingFromBackend && <LoadingIndicator/>}
      
      <UnitsContainerHeader units={units} />

      {(() => {
        const availableFloors = building.floors?.filter(floor => floor.floorType !== 'unbekannt') || [];
        
        if (availableFloors.length === 0) {
          return (
            <div className="text-center py-8 text-gray-500">
              <p>Keine Etagen mit Einheiten konfiguriert</p>
            </div>
          );
        }

        return (
          <div>
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex flex-wrap gap-2 sm:gap-4 md:gap-6 lg:gap-8">
                {availableFloors.map((floor, actualFloorIndex) => {
                  const originalFloorIndex = building.floors.findIndex(f => f === floor);
                  const floorUnits = units.filter(unit => unit.floor_id === floor.externalId);
                  
                  if (floorUnits.length === 0) return null;

                  const isActive = selectedFloorTab === actualFloorIndex;

                  return (
                    <button
                      key={actualFloorIndex}
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedFloorTab(actualFloorIndex);
                      }}
                      onTouchStart={(e) => e.preventDefault()}
                      className={`flex items-start py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm ${
                        isActive
                          ? 'border-orange-500 text-orange-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className={`inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-xs font-semibold mr-1 sm:mr-2 ${
                        isActive ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {originalFloorIndex + 1}
                      </span>
                      <div className="flex flex-col">
                        <div className="text-xs sm:text-sm font-medium leading-tight">
                          <span className="hidden sm:inline">{FLOOR_TYPES[floor.floorType].label}</span>
                          <span className="sm:hidden">{FLOOR_TYPES[floor.floorType].label}</span>
                        </div>
                        <div className="text-xs opacity-75 leading-tight">
                          ({floorUnits.length})
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Active Floor Content */}
            {availableFloors.map((floor, actualFloorIndex) => {
              if (selectedFloorTab !== actualFloorIndex) return null;

              const originalFloorIndex = building.floors.findIndex(f => f === floor);
              const consistentFloorId = floor.externalId;
              const floorUnits = units
                  .filter(unit => unit.floor_id === consistentFloorId)
                  .sort(compareUnits);
              
              if (floorUnits.length === 0) return null;

              return (
                <div key={actualFloorIndex} className="mt-6">
                  <div className="border rounded-lg bg-white">
                    <div className="px-4 py-3 bg-gray-50 border-b">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-semibold">
                            {originalFloorIndex + 1}
                          </span>
                          <div className="flex flex-col">
                            <h4 className="font-medium text-gray-800">
                              {FLOOR_TYPES[floor.floorType].label}
                            </h4>
                            <span className="text-xs text-gray-600 font-medium">
                              {floorUnits.length} Einheit{floorUnits.length !== 1 ? 'en' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-4">
                      {floorUnits.map(unit => {
                        return (
                          <UnitComponent
                            key={unit.id}
                            unit={unit}
                            building={building}
                            onDelete={handleUnitDelete}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
};

export default UnitManagement;
